import {tabSession} from '../Auth/session';
import {serverCredential} from '../Auth/credential';
import {SessionChangedError, SessionUnavailableError} from '../Auth/tabSession';
import SOCKET_OPTIONS from '../lib/socket-variables';
// A namespace import: many test files mock currentLogin with only the firm and
// user id getters, and they load this module through ErrorBoundaryO. A named
// import of getCurrentUser would fail at link time in every one of them.
import * as currentLogin from '../lib/picos/currentLogin';
import {SUPPORT_HOVER_EVENT, SUPPORT_SLOT_EVENT, SUPPORT_SLOT_SELECTOR} from './supportSlotEvent';
import {createAskBridge} from './askBridge';

// The www.outlawpractice.com support messenger (Home, Messages, Help), served
// from /support-widget/ in a same-origin iframe. It talks to Chatwoot's public
// Client API directly, so customers reach Support while our servers are down.
// Our API only supplies the signed-in user's identity hash.
// The vite dev server answers a directory URL with the app's own index.html, so dev names the file.
const WIDGET_PATH = import.meta.env.DEV ? '/support-widget/index.html' : '/support-widget/';
const SUPPORT_SITE = 'https://support.outlawpractice.com';
// Our API issues an opaque Chatwoot identifier (derived from the firm and user
// on the server, so colleagues cannot compute it) and its HMAC; both are hex.
const HEX64 = /^[0-9a-f]{64}$/;
const RETRY_MS = 60_000;
// identify() waits up to a minute per account for the signed-in user's name to load, checking every 2 seconds.
const PROFILE_WAIT_MS = 2_000;
const PROFILE_DEADLINE_MS = 60_000;
const OVERLAY_SCROLLBAR_WIDTH = 15;

// feedback opens the Report a bug or request a feature page, with that kind chosen.
type SupportOpenOptions = {help?: boolean; newConversation?: boolean; article?: string; feedback?: 'bug' | 'feature'};
// firmId and userId let the server check a bug report against the verified identifier (OWA-1870).
type Identity = {identifier: string; hash: string; name: string; email: string; firstName: string; firmId: string; userId: string};
type Issued = {identifier: string; hash: string};

let frame: HTMLIFrameElement | undefined;
let ready = false;
let opened = false;
// The identity message the widget should hold; re-sent whenever it reports ready.
let pendingIdentity: {identity: Identity | null; forget: boolean} = {identity: null, forget: false};
let identified = '';
let identifying: Promise<void> | undefined;
// Set when identify() is called while a run is in flight: one more run follows
// with the then-current account, so a switch mid-run is never dropped.
let identifyAgain = false;
// The account this tab's session was last seen ready for ('' while locked or signed out).
let sessionAccount = '';
// The account a lock or expiry detached without forgetting; logout still erases it.
let suspended = '';
let retryTimer: ReturnType<typeof setTimeout> | undefined;
// The account identify() is waiting on for a name, and when that wait ends.
let profileWait: {account: string; until: number} | undefined;

/** The launcher size and dock edge the widget was last told, so they are resent only on change. */
let launcher = {size: 0, edge: ''};

/**
 * Dock the launcher over the header's reserved slot (SupportSlot), so it never
 * covers content; the panel opens downward from there, stopping at the footer.
 * Pages without the slot keep the floating launcher, clear of the footer and
 * the scrollbar, with the panel opening upward.
 */
function positionSupport() {
    if (!frame) return;
    const footer = document.querySelector('.app-footer')?.getBoundingClientRect();
    const slot = document.querySelector(SUPPORT_SLOT_SELECTOR)?.getBoundingClientRect();
    if (slot && slot.width > 0 && slot.height > 0) {
        const size = Math.round(Math.min(slot.width, slot.height));
        const right = Math.max(0, window.innerWidth - slot.right);
        const top = Math.max(0, slot.top);
        const floor = footer && footer.height > 0 ? footer.top : window.innerHeight;
        setLauncher(size, 'top');
        frame.style.right = `${right}px`;
        frame.style.top = `${top}px`;
        frame.style.bottom = 'auto';
        frame.style.width = opened ? `${Math.min(552, Math.max(size, window.innerWidth - right - 5))}px` : `${size}px`;
        frame.style.height = opened ? `${Math.max(size, Math.min(920, floor - top - 5))}px` : `${size}px`;
        return;
    }
    setLauncher(36, 'bottom');
    frame.style.top = 'auto';
    const indicatorScrollers = [...document.querySelectorAll<HTMLElement>('.scroll-with-indicator__inner')];
    const content = indicatorScrollers.find(element => {
        const bounds = element.getBoundingClientRect();
        return bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight;
    }) ?? document.querySelector<HTMLElement>('.app__content');
    const contentBounds = content?.getBoundingClientRect();
    const contentRightInset = contentBounds && contentBounds.width > 0 ? Math.max(0, window.innerWidth - contentBounds.right) : 0;
    const measuredScrollbar = content ? Math.max(0, content.offsetWidth - content.clientWidth) :
        Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    // macOS overlay scrollbars draw over the content and take no layout width.
    // Reserve their standard width whenever this app surface is vertically scrollable.
    const scrollbar = measuredScrollbar || (content && content.scrollHeight > content.clientHeight ? OVERLAY_SCROLLBAR_WIDTH : 0);
    const bottom = (footer && footer.height > 0 ? Math.max(0, window.innerHeight - footer.top) : 0) + 5;
    frame.style.right = `${contentRightInset + scrollbar + 5}px`;
    frame.style.bottom = `${bottom}px`;
    frame.style.width = opened ? `${Math.min(552, Math.max(36, window.innerWidth - scrollbar - 10))}px` : '36px';
    frame.style.height = opened ? `${Math.max(36, Math.min(920, window.innerHeight - bottom - 5))}px` : '36px';
}

const currentTheme = () => document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
const post = (message: object) => frame?.contentWindow?.postMessage(message, location.origin);

const announceHover = (hover: boolean) => window.dispatchEvent(new CustomEvent(SUPPORT_HOVER_EVENT, {detail: hover}));

function setLauncher(size: number, edge: 'top' | 'bottom') {
    if (size === launcher.size && edge === launcher.edge) return;
    launcher = {size, edge};
    post({type: 'outlaw-chat-launcher', ...launcher});
}

// The widget forgets the context when the user changes, so each identity is followed by where that user is now.
function sendIdentity() {
    if (!ready) return;
    post({type: 'outlaw-chat-identity', ...pendingIdentity});
    post({type: 'outlaw-chat-context', context: supportContext()});
}

// Routes whose first segment names the record a bug report was written in.
const RECORD_ROUTES: Record<string, string> = {cases: 'case', sales: 'lead', contacts: 'contact', tasks: 'task'};

/**
 * Where the user is, for a bug report: the path, the case, lead, contact or task open
 * by type and ID only, and the build. A record is open at /cases/:id or, as the form
 * editors write it, /cases?item=:id; no other query value is read, since the query can
 * hold search text.
 */
export function supportContext() {
    const page = location.pathname;
    const [, segment = '', id = ''] = page.split('/');
    let recordId = '';
    try { recordId = id ? decodeURIComponent(id) : new URLSearchParams(location.search).get('item') ?? ''; } catch { /* a malformed path names no record */ }
    const record = RECORD_ROUTES[segment] && recordId ? {record_type: RECORD_ROUTES[segment], record_id: recordId} : {};
    const build = typeof __BUILD_VERSION__ === 'string' ? `${__BUILD_VERSION__}${typeof __BUILD_GIT_HASH__ === 'string' ? ` (${__BUILD_GIT_HASH__})` : ''}` : '';
    return {page, ...record, app_version: build, browser: navigator.userAgent};
}

/** Create the widget iframe once. */
function mountWidget() {
    if (frame) return;
    frame = document.createElement('iframe');
    frame.title = 'Outlaw Practice support';
    frame.src = WIDGET_PATH;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.style.cssText = 'position:fixed;right:20px;bottom:20px;width:36px;height:36px;border:0;z-index:2147483001;background:transparent;color-scheme:normal';
    frame.addEventListener('load', () => post({type: 'outlaw-chat-host-ready', theme: currentTheme(), launcher}));
    document.body.append(frame);
    positionSupport();
}

// The Ask tab (OWA-1872): AI search through this tab's own sign-in, never through Chatwoot.
// It is for Firm Users only, never in the Client Portal, as the AI search button was. A
// test's currentLogin mock may lack isPortalUser; real code always has it.
const askAccount = () => {
    if (location.pathname.includes('portal')) return '';
    try { if (currentLogin.isPortalUser?.()) return ''; } catch { return ''; }
    return currentIdentifier();
};
const newAskBridge = () => createAskBridge(message => post(message), askAccount);
let ask = newAskBridge();

const currentIdentifier = () => {
    const firmId = currentLogin.getCurrentFirmId(), userId = currentLogin.getCurrentUserId();
    return firmId && userId && tabSession.isLocalReady() ? `${firmId}:${userId}` : '';
};

const cacheKey = (account: string) => `chatwoot_identity:${account}`;
const validIssued = (value: any): value is Issued => HEX64.test(value?.identifier) && HEX64.test(value?.hash);

/** The identity our API last issued to this browser for the signed-in account, if any. */
function knownIdentity(account: string): Issued | undefined {
    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey(account)) ?? 'null');
        return validIssued(cached) ? {identifier: cached.identifier, hash: cached.hash} : undefined;
    } catch { return undefined; }
}

/**
 * Ask our API for the hash. Without one, `retry` says whether the API was
 * unreachable (network failure or a 5xx, so try again) or answered with a
 * refusal (for example 404 when the server has no inbox token), where polling
 * every minute would only add load.
 */
async function fetchIdentity(account: string): Promise<{issued?: Issued; retry?: boolean}> {
    let operation;
    try { operation = tabSession.capture(); } catch { return {}; }
    if (`${operation.identity.firmId}:${operation.identity.userId}` !== account) return {};
    try {
        // This tab's own credentials, renewed when they are about to expire.
        const credential = serverCredential(await tabSession.credentials(operation));
        const base = `${SOCKET_OPTIONS.secure ? 'https' : 'http'}://${SOCKET_OPTIONS.hostname}`;
        const response = await fetch(new URL('/support/identity', base), {
            method: 'POST', headers: {Authentication: `Bearer ${credential}`}, cache: 'no-store', credentials: 'omit',
            signal: AbortSignal.any([operation.signal, AbortSignal.timeout(15000)]),
        });
        operation.assertCurrent();
        if (!response.ok) return {retry: response.status >= 500};
        const value = await response.json();
        operation.assertCurrent();
        // The server echoes the account it authenticated (firmId:userId from the
        // JWT). The opaque identifier cannot be checked here, so the echo binds the
        // response to this tab's account: a token from another firm or user is refused.
        if (value?.account !== account || !validIssued(value)) return {};
        return {issued: {identifier: value.identifier, hash: value.hash}};
    } catch (error) {
        // A locked, signed-out or switched session is not an outage: the session
        // listener below identifies again once this tab is signed in.
        if (error instanceof SessionChangedError || error instanceof SessionUnavailableError) return {};
        return {retry: true};
    }
}

/** Hand the signed-in user to the widget, which personalizes and verifies the chat contact. */
function attach(account: string, {identifier, hash}: Issued) {
    const user = currentLogin.getCurrentUser(), contact = currentLogin.getCurrentUserContact();
    const first = contact?.first?.trim() ?? '';
    const name = [first, contact?.last?.trim()].filter(Boolean).join(' ') || user?.email || '';
    const [firmId = '', userId = ''] = account.split(':');
    pendingIdentity = {identity: {identifier, hash, name, email: user?.email ?? '', firstName: first, firmId, userId}, forget: false};
    identified = account; suspended = '';
    sendIdentity();
}

/**
 * Attach the signed-in user to the widget. A hash this browser already has
 * authenticates at once, offline included. Our API is then asked for a fresh
 * one, which is cached for the next offline start. With no hash at all
 * (first use while our API is unreachable) the widget stays an unauthenticated
 * session, and this retries when the browser comes back online and, while the
 * API is unreachable, every minute, so the session authenticates once the API
 * answers.
 */
function identify(): Promise<void> {
    if (identifying) { identifyAgain = true; return identifying; }
    identifying = (async () => {
        clearTimeout(retryTimer); retryTimer = undefined;
        const account = currentIdentifier();
        if (!account || account === identified) return;
        // The name comes from the user's contact record, which can load after the
        // login itself (on a reload this first runs at mount). Wait for it so the
        // greeting and Support see the name; after the waits, go on without it.
        const user = currentLogin.getCurrentUser();
        if (!user || (user.contactId && !currentLogin.getCurrentUserContact())) {
            if (profileWait?.account !== account) profileWait = {account, until: Date.now() + PROFILE_DEADLINE_MS};
            if (Date.now() < profileWait.until) {
                retryTimer = setTimeout(() => void identify(), PROFILE_WAIT_MS);
                return;
            }
        }
        profileWait = undefined;
        const known = knownIdentity(account);
        if (known) attach(account, known);
        const {issued, retry} = await fetchIdentity(account);
        if (account !== currentIdentifier()) return;
        if (issued) {
            try { localStorage.setItem(cacheKey(account), JSON.stringify(issued)); } catch { /* storage full or blocked */ }
            if (issued.identifier !== known?.identifier || issued.hash !== known?.hash) attach(account, issued);
        }
        else if (retry && !known) retryTimer = setTimeout(() => void identify(), RETRY_MS);
    })().finally(() => {
        identifying = undefined;
        if (identifyAgain) { identifyAgain = false; void identify(); }
    });
    return identifying;
}

/** Logout: end the signed-in user's chat identity and forget their stored visitor and hash. */
export function resetSupport() {
    clearTimeout(retryTimer); retryTimer = undefined; profileWait = undefined;
    const account = identified || suspended;
    if (account) {
        try { localStorage.removeItem(cacheKey(account)); } catch { /* storage blocked */ }
        pendingIdentity = {identity: null, forget: true};
        sendIdentity();
    }
    identified = ''; suspended = '';
}

/** Show the launcher, and identify the user when signed in. Safe to call often. */
export function initializeSupport() {
    mountWidget();
    // Portal mode is settled after sign-in, so the app's calls here re-check Ask.
    ask.accountChanged();
    void identify();
}

export function openSupport(options: SupportOpenOptions = {}) {
    if (options.article !== undefined) {
        try {
            const url = new URL(options.article, SUPPORT_SITE);
            if (url.origin !== SUPPORT_SITE || url.username || url.password) return;
            // The messenger's Help tab shows the support site; before it is up, a new tab does.
            if (ready) post({type: 'outlaw-chat-open', article: url.href});
            else window.open(url.href, '_blank', 'noopener,noreferrer');
        } catch { /* ignore a malformed article link */ }
        return;
    }
    if (ready) {
        const feedback = options.feedback === 'bug' || options.feedback === 'feature' ? options.feedback : undefined;
        post({type: 'outlaw-chat-open', help: options.help === true, newConversation: options.newConversation === true, feedback});
        void identify();
        return;
    }
    // The messenger is not up yet. Open the support site now, while the click
    // still allows a new tab: a tab opened after an await can be blocked as a
    // popup, and navigating this tab would discard the user's unsaved work.
    window.open(SUPPORT_SITE, '_blank', 'noopener,noreferrer');
    initializeSupport();
}

window.addEventListener('message', event => {
    if (!frame?.contentWindow || event.source !== frame.contentWindow || event.origin !== location.origin) return;
    if (event.data?.type === 'outlaw-chat-size') {
        opened = event.data.opened === true;
        positionSupport();
        // The open panel replaces the tooltip, and a report written in it records where the user is now.
        if (opened) { announceHover(false); post({type: 'outlaw-chat-context', context: supportContext()}); }
    }
    else if (event.data?.type === 'outlaw-chat-hover') announceHover(event.data.hover === true && !opened);
    // The report page asks again as it opens: the user may have moved since the panel opened.
    else if (event.data?.type === 'outlaw-chat-context-request') post({type: 'outlaw-chat-context', context: supportContext()});
    else if (event.data?.type === 'outlaw-chat-ready') {
        ready = true;
        post({type: 'outlaw-chat-theme', theme: currentTheme()});
        sendIdentity();
        ask.ready();
    }
    else ask.message(event.data);
});
// Close the open panel on a press anywhere else on the page or on Escape (OWA-1971). A press
// inside the messenger lands in its iframe's own document and never reaches this one.
document.addEventListener('pointerdown', () => { if (opened) post({type: 'outlaw-chat-close'}); }, true);
window.addEventListener('keydown', event => { if (opened && event.key === 'Escape') post({type: 'outlaw-chat-close'}); });
window.addEventListener('resize', positionSupport);
// SupportSlot announces when the header slot mounts, moves or unmounts.
window.addEventListener(SUPPORT_SLOT_EVENT, positionSupport);
window.addEventListener('online', () => void identify());
// Sign-in is per tab: other tabs' logins never change this one. When this tab
// locks, expires or switches account, show the anonymous visitor (keeping the
// stored visitor, unlike logout), and identify again once it is signed in.
tabSession.subscribe(() => {
    const account = currentIdentifier();
    if (account === sessionAccount) return;
    sessionAccount = account;
    ask.accountChanged();
    if (identified && account !== identified) {
        clearTimeout(retryTimer); retryTimer = undefined; profileWait = undefined;
        pendingIdentity = {identity: null, forget: false};
        sendIdentity();
        suspended = identified;
        identified = '';
    }
    if (account && frame) void identify();
});

/** Test-only: forget module state between cases (browser-mode tests share one module instance). */
export const __test__ = {
    reset: () => {
        clearTimeout(retryTimer); retryTimer = undefined;
        frame?.remove(); frame = undefined; ready = false; opened = false; launcher = {size: 0, edge: ''};
        pendingIdentity = {identity: null, forget: false};
        identifying = undefined; identifyAgain = false; identified = ''; suspended = ''; profileWait = undefined;
        // The session itself is not reset: track it from where it stands.
        sessionAccount = currentIdentifier();
        ask = newAskBridge();
    },
    frame: () => frame,
};
